import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {
  ChartingHistorySelectorComponent,
  ContentContainerComponent,
} from '@principle-theorem/ng-principle-shared';
import { PipesModule, TypedFormControl } from '@principle-theorem/ng-shared';
import {
  IChartHistoryItem,
  IClinicalChart,
  IPerioRecord,
  PerioMeasurement,
} from '@principle-theorem/principle-core/interfaces';
import { getEnumValues, snapshot, WithRef } from '@principle-theorem/shared';
import { pick } from 'lodash';
import { ReplaySubject } from 'rxjs';

@Component({
  selector: 'pr-perio-chart-create',
  imports: [
    CommonModule,
    ContentContainerComponent,
    MatButtonModule,
    MatIconModule,
    ChartingHistorySelectorComponent,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    PipesModule,
  ],
  templateUrl: './perio-chart-create.component.html',
  styleUrl: './perio-chart-create.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioChartCreateComponent {
  chartHistory$ = new ReplaySubject<IChartHistoryItem[]>(1);
  selectedChart$ = new ReplaySubject<WithRef<IClinicalChart>>(1);
  measurements = getEnumValues(PerioMeasurement).filter(
    (measurement) => measurement !== PerioMeasurement.CAL
  );
  measurementsCtrl = new TypedFormControl<PerioMeasurement[]>(
    this.measurements,
    [Validators.required]
  );

  @Output() perioRecordsCreated = new EventEmitter<IPerioRecord[]>();

  @Input()
  set chartHistory(chartHistory: IChartHistoryItem[]) {
    if (chartHistory) {
      this.chartHistory$.next(chartHistory);
      if (chartHistory.length) {
        this.selectedChart$.next(chartHistory[0].value);
      }
    }
  }

  isUppercaseMeasurement(measurement: PerioMeasurement): boolean {
    return [PerioMeasurement.MGJ, PerioMeasurement.CAL].includes(measurement);
  }

  createPerioRecords(): void {
    this.perioRecordsCreated.emit([]);
  }

  async copyPerioRecords(): Promise<void> {
    const chart = await snapshot(this.selectedChart$);
    const perioRecords = chart.perioRecords;

    if (!perioRecords || !perioRecords.length || !this.measurementsCtrl.value) {
      return;
    }

    const copiedRecords = perioRecords.map((record) => ({
      ...record,
      data: pick(record.data, this.measurementsCtrl.value),
    }));

    this.perioRecordsCreated.emit(copiedRecords);
  }
}
